// src/services/api.js
import axios from "axios";
import i18n from "../i18n";

const trimTrailingSlash = (value) => String(value || "").replace(/\/+$/, "");

const defaultApiBase = trimTrailingSlash(
  process.env.REACT_APP_API_BASE_URL ||
    (typeof window !== "undefined" && window.location?.origin
      ? `${window.location.origin}/api`
      : "http://localhost:5002/api")
);

const defaultFrontendBase = trimTrailingSlash(
  process.env.REACT_APP_FRONTEND_URL ||
    (typeof window !== "undefined" && window.location?.origin
      ? window.location.origin
      : "http://localhost:3000")
);

const defaultUploadBase = trimTrailingSlash(
  process.env.REACT_APP_UPLOAD_API_BASE || defaultFrontendBase
);

const apiBase = (explicit, fallbackPath) =>
  explicit || `${defaultApiBase}${fallbackPath}`;


function toApiLang(lang) {
  if (lang === "el") return "gr";
  return lang || "en";
}

const createApiInstance = (baseURL) => {
  const instance = axios.create({
    baseURL,
    withCredentials: true,
    headers: { "Content-Type": "application/json" },
  });
  instance.interceptors.request.use(
    (config) => {
      let currentLang = i18n.language || "en";
      // Εδώ κάνε mapping το 'el' σε 'gr'
      if (currentLang === "el") currentLang = "gr";
      console.log("[API] Χρήση γλώσσας:", currentLang); // θα δεις αν είναι 'gr'
      const token = localStorage.getItem("auth_token");
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      config.params = { ...config.params, lang: currentLang };
      return config;
    },
    (error) => Promise.reject(error)
  );

  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      console.error("API Error:", error.response ? error.response.data : error.message);
      return Promise.reject(error);
    }
  );
  return instance;
};



const api_users = createApiInstance(apiBase(process.env.REACT_APP_USERS_ROUTES_API_BASE_URL, "/users"));
const api_household = createApiInstance(apiBase(process.env.REACT_APP_HOUSEHOLD_ROUTES_API_BASE_URL, "/household"));
const api_recipes = createApiInstance(apiBase(process.env.REACT_APP_RECIPES_ROUTES_API_BASE_URL, "/recipes"));
const api_inventory = createApiInstance(apiBase(process.env.REACT_APP_INVENTORY_ROUTES_API_BASE_URL, "/inventory"));
const api_meal_plan = createApiInstance(apiBase(process.env.REACT_APP_MEAL_PLAN_ROUTES_API_BASE_URL, "/meal_plan"));
const api_products = createApiInstance(apiBase(process.env.REACT_APP_PRODUCTS_ROUTES_API_BASE_URL, "/products"));
const api_pricing = createApiInstance(apiBase(process.env.REACT_APP_PRICING_ROUTES_API_BASE_URL, "/pricing"));
const api_generic_products = createApiInstance(apiBase(process.env.REACT_APP_GENERIC_PRODUCTS_ROUTES_API_BASE_URL, "/generic_products"));
const api_variants = createApiInstance(apiBase(process.env.REACT_APP_VARIANT_PRODUCTS_API_BASE_URL, "/variant_products"));
const api_favorites = createApiInstance(apiBase(process.env.REACT_APP_FAVORITES_ROUTES_API_BASE_URL, "/favorites"));
const api_stripe = createApiInstance(apiBase(process.env.REACT_APP_STRIPE_ROUTES_API_BASE_URL, "/stripe"));
const api_public = createApiInstance(apiBase(process.env.REACT_APP_PUBLIC_API_BASE_URL, "/public"));
const api_reviews = createApiInstance(apiBase(process.env.REACT_APP_REVIEWS_ROUTES_API_BASE_URL, "/app_reviews"));
const api_shopping_list = createApiInstance(apiBase(process.env.REACT_APP_SHOPPING_ROUTES_API_BASE_URL, "/shopping_list"));
const api_shopping_preview = createApiInstance(apiBase(process.env.REACT_APP_SHOPPING_PREVIEW_API_BASE_URL, "/shopping_list_preview"));
const api_instacart = createApiInstance(apiBase(process.env.REACT_APP_INSTACART_API_BASE_URL, "/instacart"));
const api_feedback = createApiInstance(
  apiBase(process.env.REACT_APP_FEEDBACK_ROUTES_API_BASE_URL, "/feedback")
);
const api_admin_analytics = createApiInstance(
  apiBase(process.env.REACT_APP_ADMIN_ANALYTICS_API_BASE_URL, "/admin/analytics")
);



const feedbackAPI = {
  adminList: (params = {}) => api_feedback.get("/admin/list", { params }),
};

const analyticsAPI = {
  funnel: (days = 7) => api_admin_analytics.get("/funnel", { params: { days } }),
};


const usersAPI = {
  login: (email, password) => api_users.post('/login', { email, password }),
  register: (username, email, password) => api_users.post('/register', { username, email, password }),
  logout: () => api_users.post('/logout'),
  getCurrentUser: () => api_users.get('/current_user'),
  adminDashboard: () => api_users.get('/admin_dashboard'),
  addAdmin: (username, email, password) => api_users.post('/add_admin', { username, email, password }),
  requestPasswordReset: (email) => api_users.post('/request_password_reset', { email }),
  resetPassword: (token, new_password) => api_users.post('/confirm_password_reset', { token, new_password }),
  requestTrialRenewal: (email) => api_users.post('/request_trial_renewal', { email }),
  googleLogin: (data) => api_users.post('/login/google', data),
  getUsersStats: () => api_users.get('/admin/users_stats'),
};


const householdAPI = {
  getProfile: () => api_household.get('/profile'),
  saveProfile: (data) => api_household.post('/profile', data),
  getDietTypes: () => api_household.get('/diet_types'),
  getCountryRecipes: () => api_household.get('/country_recipes'),
  getReligiousPreferences: () => api_household.get('/religious_preferences'),
  getFoodTypes: () => api_household.get('/food_types'),
  getMealTypes: () => api_household.get('/meal_types'),
  getSeasons: () => api_household.get('/seasons'),
};

const inventoryAPI = {
  list: () => api_inventory.get('/'),
  addOrUpdate: (data) => api_inventory.post('/', data),
  check: () => api_inventory.get('/check'),
};

const recipesAPI = {
  list: (lang = "en") => api_recipes.get('/list', { params: { lang: toApiLang(lang) } }),

  getOne: (id, { params = {} } = {}) => {
    const newParams = { ...params, lang: toApiLang(params.lang) };
    return api_recipes.get(`/${id}`, { params: newParams });
  },

  add: (data, lang = "en") => api_recipes.post('/add', { ...data, lang: toApiLang(lang) }),

  update: (recipeId, recipeData, lang = "en") =>
    api_recipes.post(`/update/${recipeId}`, { ...recipeData, lang: toApiLang(lang) }),

  delete: (recipeId, lang = "en") =>
    api_recipes.post(`/delete/${recipeId}`, { lang: toApiLang(lang) }),

  findByIngredients: (ingredients, lang = "en", country = 1, household_members = 1, max_missing = 2) =>
    api_recipes.post('/find_by_ingredients', {
      ingredients,
      lang: toApiLang(lang),
      country_id: country,
      household_members,
      max_missing,
    }),

  findByName: (name, lang = "en") =>
    api_recipes.get('/search', { params: { query: name, lang: toApiLang(lang) } }),

  getAllGenericProducts: (lang = "en") =>
    api_generic_products.get('/', { params: { lang: toApiLang(lang) } }),

  search: (params = {}) => {
    const paramsWithLang = { ...params, lang: toApiLang(params.lang) };
    return api_recipes.get('/search', {
      params: Object.fromEntries(
        Object.entries(paramsWithLang).filter(([_, v]) => v !== undefined && v !== null && v !== "")
      )
    });
  },

  // uploadImage: (formData, lang = "en") =>
  //   api_recipes.post('/upload_image', formData, {
  //     headers: { 'Content-Type': 'multipart/form-data' },
  //     params: { lang: toApiLang(lang) },
  //   }),

  myList: (lang = "en") => api_recipes.get('/my_recipes', { params: { lang: toApiLang(lang) } }),

  uploadImage: (fd) =>
    api_recipes.post(`/upload_image`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
      withCredentials: true,
    }),

  setImageUrl: (recipeId, imageUrl) =>
    api_recipes.post(`/set_image_url/${recipeId}`, { image_url: imageUrl }, {
      withCredentials: true,
    }),

};


const mealPlanAPI = {
  viewExisting: (profileId) => api_meal_plan.get(`/view_existing_meal_plan/${profileId}`),
  update: (profileId, data) => api_meal_plan.post(`/update_meal_plan/${profileId}`, data),
  save: (data) => api_meal_plan.post('/save', data),
  getUserProfile: (userId) => api_meal_plan.get(`/get_user_profile`, { params: { user_id: userId } }),
  getSavedMealPlan: (params) => api_meal_plan.get("/get_saved", { params }),
  generate: (profileId, params) => api_meal_plan.get(`/generate_meal_plan/${profileId}`, { params }),
};

const shoppingListAPI = {
  // ---- MAIN shopping_list routes (/api/shopping_list) ----
  generate: () => api_shopping_list.post("/generate"),
  updateItem: (item_id, quantity_purchased) =>
    api_shopping_list.post("/update_item", { item_id, quantity_purchased }),
  getUserLists: () => api_shopping_list.get("/lists"),
  list: (country_id = 1, lang = "en") =>
    api_shopping_list.get("/", { params: { country_id, lang } }),
  getById: (id) => api_shopping_list.get(`/shopping_list/${id}`),
  aggregate: (params) => api_shopping_list.get("/aggregate", { params }),

  saveSelected: (payload) => api_shopping_list.post("/save_selected", payload),
  resolveList: (payload) => api_shopping_list.post("/resolve_list", payload),
  lockListToCurrentBest: (payload) => api_shopping_list.post("/lock", payload),

  saveList: (payload) => api_shopping_list.post("/lists/save", payload),
  listAll: () => api_shopping_list.get("/lists"),

  updateStore: (item_id, product_variant_id) =>
    api_shopping_list.post("/update_store", { item_id, product_variant_id }),

  createRecipeShoppingListSafe: (recipeId, members) =>
    api_shopping_list.post("/create_recipe_safe", {
      recipe_id: recipeId,
      household_members: members,
    }),

  // ---- BACKWARD COMPATIBILITY (preview routes moved) ----
  // ✅ αυτά ΠΡΕΠΕΙ να υπάρχουν γιατί τα καλούν τα pages
  preview: (payload) => shoppingListPreviewAPI.preview(payload),
  previewCustom: (payload) => shoppingListPreviewAPI.previewCustom(payload),
  previewForRecipe: (recipe_id, household_members = 1, country_id = 1, lang = "en") =>
    shoppingListPreviewAPI.previewForRecipe({
      recipe_id,
      household_members,
      country_id,
      lang,
    }),
  previewFromMealPlan: (payload) => shoppingListPreviewAPI.previewFromMealPlan(payload),
  recalculate: (payload) => shoppingListPreviewAPI.recalculate(payload),
  getRemainingCost: (lang) => shoppingListPreviewAPI.remainingCost(lang),

  // ---- BACKWARD COMPATIBILITY (instacart routes moved) ----
  getUSSupportedLocations: () => instacartAPI.usSupportedLocations(),
  getAvailableStores: (generic_product_id, params = {}) => instacartAPI.availableStores(generic_product_id, params),
  availableStores: (generic_product_id, params = {}) => instacartAPI.availableStores(generic_product_id, params),

  instacartPreview: (payload) => instacartAPI.instacartPreview(payload),
  checkoutInstacart: (payload) => instacartAPI.checkoutInstacart(payload),
  checkoutInstacartFromList: (payload) => instacartAPI.checkoutInstacartFromList(payload),
  instacartClick: (payload) => instacartAPI.instacartClick(payload),
};

const shoppingListPreviewAPI = {
  preview: (payload) => api_shopping_preview.post("/preview", payload),
  previewCustom: (payload) => api_shopping_preview.post("/preview_custom", payload),
  previewForRecipe: (payload) => api_shopping_preview.post("/preview_for_recipe", payload),
  previewFromMealPlan: (payload) => api_shopping_preview.post("/preview_from_mealplan", payload),
  recalculate: (payload) => api_shopping_preview.post("/recalculate", payload),
  remainingCost: (lang) => api_shopping_preview.get("/remaining_cost", { params: { lang } }),
  krogerCartItemsFromList: (payload) =>
    api_shopping_preview.post("/kroger/cart_items_from_list", payload),
};

const instacartAPI = {
  usSupportedLocations: () => api_instacart.get("/us_supported_locations"),
  availableStores: (generic_product_id, params = {}) =>
    api_instacart.get(`/available_stores/${generic_product_id}`, { params }),
  variantsForGeneric: (generic_product_id, params = {}) =>
    api_instacart.get(`/variants_for_generic/${generic_product_id}`, { params }),

  instacartPreview: (payload) => api_instacart.post("/checkout/instacart_preview", payload),
  checkoutInstacart: (payload) => api_instacart.post("/checkout/instacart", payload),
  checkoutInstacartFromList: (payload) => api_instacart.post("/checkout/instacart_from_list", payload),
  instacartClick: (payload) => api_instacart.post("/checkout/instacart_click", payload),
};


const productsAPI = {
  list: () => api_products.get('/'),
  add: (data) => api_products.post('/', data),
  update: (id, data) => api_products.put(`/${id}`, data),
  get: (id) => api_products.get(`/${id}`),
  search: (query) => api_products.get('/search', { params: { query } }),
};

const pricingAPI = {
  cheapest: (recipeId) => api_pricing.get('/cheapest', { params: { recipe_id: recipeId } }),
};

const favoritesAPI = {
  add: (data) => api_favorites.post('/add', data),
  remove: (recipe_id) => api_favorites.delete(`/remove/${recipe_id}`)
};

const appReviewsAPI = {
  list: () => api_reviews.get('/app_reviews'),
  add: (data) => api_reviews.post('/app_reviews', data),
};

const genericProductsAPI = {
  list: () => api_generic_products.get('/'),
  add: (data) => api_generic_products.post('/', data),
  update: (id, data) => api_generic_products.put(`/${id}`, data),
  get: (id) => api_generic_products.get(`/${id}`),
  delete: (id) => api_generic_products.delete(`/${id}`),
  suggest: (data) => api_generic_products.post('/suggest', data)
};

const variantsAPI = {
  list: () => api_variants.get("/"),
  add: (data) => api_variants.post("/", data),
  update: (id, data) => api_variants.put(`/${id}`, data)
};

const stripeAPI = {
  createCheckoutSession: (payload) => api_stripe.post('/create_checkout_session', payload),
  webhook: (data, headers = {}) => api_stripe.post('/webhook', data, { headers }),
  getActivePrices: (countryId) => api_public.get('/pricing-catalog', { params: { country_id: countryId } }),
};


export async function uploadRecipeImageToPlesk({ file, recipeId, slug }) {
  const base = defaultUploadBase;
  const url = `${base}/upload-api/recipe-image.php?debug=1`;

  const key = process.env.REACT_APP_RECIPE_UPLOAD_KEY || "";
  if (!key) throw new Error("Missing REACT_APP_RECIPE_UPLOAD_KEY in frontend env.");
  if (!file) throw new Error("No file selected.");
  if (!recipeId) throw new Error("Missing recipeId.");

  const fd = new FormData();
  fd.append("file", file);
  fd.append("recipe_id", String(recipeId));
  fd.append("slug", slug || "");
  fd.append("upload_key", key); // ✅ ΚΡΙΣΙΜΟ: key μέσα στο body

  const res = await fetch(url, {
    method: "POST",
    body: fd,
    redirect: "manual",
  });

  if (res.type === "opaqueredirect" || (res.status >= 300 && res.status < 400)) {
    throw new Error(`Redirect detected (${res.status}). Fix base domain (www vs non-www).`);
  }

  const data = await res.json().catch(() => null);
  if (!res.ok || !data?.ok) throw new Error(data?.error || `Upload failed (HTTP ${res.status})`);

  return data.path;
}


// export async function uploadToFlask({ file, recipeId, slug }) {
//   const fd = new FormData();
//   fd.append("image", file);              // <-- ίδιο key με Flask
//   fd.append("recipe_id", String(recipeId));
//   fd.append("slug", slug || "");

//   const res = await fetch(`${defaultApiBase}/recipes/upload_image`, {
//     method: "POST",
//     body: fd,
//     credentials: "include",              // <-- αν είσαι login_required με cookie session
//   });

//   const data = await res.json().catch(() => ({}));
//   if (!res.ok || !data.success) throw new Error(data.error || `HTTP ${res.status}`);
//   return data.image_url;                 // "/uploads/recipes/...."
// }


const apiExports = {
  users: usersAPI,
  household: householdAPI,
  recipes: recipesAPI,
  inventory: inventoryAPI,
  mealPlan: mealPlanAPI,
  shoppingList: shoppingListAPI,
  products: productsAPI,
  pricing: pricingAPI,
  favorites: favoritesAPI,
  genericProducts: genericProductsAPI,
  variants: variantsAPI,
  stripe: stripeAPI,
  appReviews: appReviewsAPI,
  shoppingListPreview: shoppingListPreviewAPI,
  instacart: instacartAPI,
  feedback: feedbackAPI,
  analytics: analyticsAPI,
};

export {
  usersAPI,
  householdAPI,
  recipesAPI,
  inventoryAPI,
  mealPlanAPI,
  shoppingListAPI,
  productsAPI,
  pricingAPI,
  favoritesAPI,
  genericProductsAPI,
  variantsAPI,
  stripeAPI,
  appReviewsAPI,
  shoppingListPreviewAPI,
  instacartAPI,
  feedbackAPI,
  analyticsAPI,
};

export default apiExports;
